<template>
  <el-dialog :model-value="modelValue" append-to-body top="5vh" class="gold-business-dialog" :title="dialogTitle" width="90%" @update:model-value="$emit('update:modelValue',$event)" @closed="reset">
    <div v-loading="loading" class="gold-document-detail">
      <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon>
        <template #default><el-button link type="primary" @click="load">重新加载</el-button></template>
      </el-alert>
      <template v-else-if="payload">
        <el-alert v-if="statusRow" :title="'当前状态：'+statusText(statusRow.value)" :description="statusTooltip(statusRow.context,statusRow.row)" type="info" :closable="false" show-icon />
        <el-descriptions :column="3" border class="detail-summary">
          <el-descriptions-item v-for="item in summaryItems" :key="item.label" :label="item.label">
            <el-link v-if="item.document && item.document.id" type="primary" :underline="false" @click="openDocument(item.document)">{{ display(item.value) }}</el-link>
            <span v-else>{{ display(item.value) }}</span>
          </el-descriptions-item>
        </el-descriptions>
        <section v-for="table in detailTables" :key="table.title" class="detail-section">
          <h3>{{ table.title }}</h3>
          <el-table :data="table.rows" border stripe empty-text="暂无记录">
            <el-table-column v-for="column in table.columns" :key="column.label" :label="column.label" :min-width="column.width || 120" :align="column.align" show-overflow-tooltip>
              <template #default="scope">
                <el-tooltip v-if="column.status" :content="statusTooltip(column.status,scope.row)" placement="top">
                  <span class="gold-status-help" tabindex="0"><el-tag size="small" effect="plain">{{ statusText(value(scope.row,column.prop)) }}</el-tag></span>
                </el-tooltip>
                <el-link v-else-if="column.document && column.document(scope.row)?.id" type="primary" :underline="false" @click="openDocument(column.document(scope.row))">
                  {{ display(column.format ? column.format(scope.row) : value(scope.row,column.prop)) }}
                </el-link>
                <span v-else>{{ display(column.format ? column.format(scope.row) : value(scope.row,column.prop)) }}</span>
              </template>
            </el-table-column>
          </el-table>
        </section>
      </template>
    </div>
    <template #footer>
      <el-button v-if="history.length" @click="goBack">返回上级单据</el-button>
      <el-button @click="$emit('update:modelValue',false)">关闭</el-button>
    </template>
  </el-dialog>
</template>

<script>
import request from '@/utils/request'
import {
  getGoldDemandDetail, getGoldPurchaseDetail, getGoldReceiptDetail,
  getGoldTransferDetail, getGoldDebtDetail
} from '@/api/gold'
import { statusText, statusTooltip } from './goldWorkflow'

const titles = {
  demand: '金料需求明细', purchase: '金料采购单详情', payment: '采购付款单详情', receipt: '金料收货批次详情',
  transfer: '金料调拨单详情', debt: '金料欠料单详情', purchase_order: '采购订单详情',
  purchase_delivery: '采购到货单详情', stock_bound: '库存来源详情'
}

export default {
  name: 'GoldDocumentDetailDialog',
  props: {
    modelValue: Boolean,
    document: { type: Object, default: () => null }
  },
  emits: ['update:modelValue'],
  data() {
    return { activeDocument: null, payload: null, loading: false, error: '', history: [], requestID: 0 }
  },
  computed: {
    dialogTitle() {
      const doc = this.activeDocument || this.document || {}
      return `${titles[doc.type] || '单据详情'}${doc.billCode ? ` · ${doc.billCode}` : ''}`
    },
    statusRow() {
      const p = this.payload || {}
      const type = this.activeDocument?.type
      if (type === 'demand' && p.bill) return { context: 'demand', value: p.bill.status, row: p.bill }
      if (type === 'purchase' && p.purchase) return { context: 'purchase', value: p.purchase.displayStatus, row: { ...p.purchase, status: p.purchase.displayStatus } }
      if (type === 'transfer' && p.transfer) return { context: 'transfer', value: p.transfer.status, row: p.transfer }
      if (type === 'debt' && p.debt) return { context: 'debt', value: p.debt.status, row: p.debt }
      if (type === 'payment' && p.payment) return { context: 'payment', value: p.payment.status, row: p.payment }
      return null
    },
    summaryItems() {
      const p = this.payload || {}
      const type = this.activeDocument?.type
      if (type === 'demand') {
        const b = p.bill || {}
        return [
          this.item('需求单', b.billCode), this.item('公司', b.companyName), this.item('状态', statusText(b.status)),
          this.item('申请克重', this.grams(b.requestedQuantity)), this.item('批准克重', this.grams(b.approvedQuantity)), this.item('外采总金重', this.grams(b.totalGoldQuantity)),
          this.item('供应商', this.supplier(b)), this.item('统一实际金价', this.money(b.unitPrice, 4)), this.item('采购总额', this.money(b.purchaseTotalAmount)),
          this.item('付款单', b.paymentBillCode, this.doc('payment', b.paymentFlowInstanceID, b.paymentBillCode)), this.item('付款金额', this.money(b.paymentAmount)), this.item('创建时间', b.createDate)
        ]
      }
      if (type === 'purchase') {
        const b = p.purchase || {}
        return [
          this.item('采购单', b.billCode), this.item('需求单', b.demandBillCode, this.doc('demand', b.demandBillID, b.demandBillCode)), this.item('供应商', this.supplier(b)),
          this.item('申请部门', b.departmentName || b.departmentID), this.item('材质', b.materialName || b.materialCode), this.item('自动匹配账户', b.targetAccountEnabled ? (b.targetAccountName || b.departmentName) : '未启用'),
          this.item('采购克重', this.grams(b.purchaseQuantity)), this.item('实际金价', this.money(b.unitPrice, 4)),
          this.item('采购总额', this.money(b.totalAmount)), this.item('已收货', this.grams(b.receivedQuantity)), this.item('已分配', this.grams(b.allocatedQuantity)),
          this.item('剩余待收货', this.grams(b.transitQuantity)), this.item('付款单', b.paymentBillCode, this.doc('payment', b.paymentFlowInstanceID, b.paymentBillCode)), this.item('付款状态', statusText(b.paymentStatus))
        ]
      }
      if (type === 'receipt') {
        const b = p.receipt || {}
        return [
          this.item('收货批次', b.receiptCode), this.item('采购单', b.purchaseBillCode, this.doc('purchase', b.purchaseID, b.purchaseBillCode)), this.item('需求单', b.demandBillCode, this.doc('demand', b.demandBillID, b.demandBillCode)),
          this.item('申请部门', b.departmentName || b.departmentID), this.item('供应商', this.supplier(b)), this.item('材质', b.materialName || b.materialCode),
          this.item('自动匹配账户', b.targetAccountEnabled ? (b.targetAccountName || b.departmentName) : '未启用'), this.item('收货克重', this.grams(b.quantity)),
          this.item('实际金价', this.money(b.unitPrice, 4)), this.item('收货金额', this.money(b.amount)), this.item('收货时间', b.receiptDate), this.item('备注', b.remark)
        ]
      }
      if (type === 'transfer') {
        const b = p.transfer || {}
        return [
          this.item('调拨单', b.billCode), this.item('需求单', b.demandBillCode, this.doc('demand', b.demandBillID, b.demandBillCode)), this.item('材质', b.fromMaterialName || b.materialCode),
          this.item('调出账户', b.fromAccountName), this.item('调入账户', b.toAccountName), this.item('调拨克重', this.grams(b.quantity)),
          this.item('确认人', b.confirmUserName), this.item('确认时间', b.confirmDate), this.item('备注', b.remark)
        ]
      }
      if (type === 'debt') {
        const b = p.debt || {}
        return [
          this.item('欠料单', b.debtCode), this.item('供应商', this.supplier(b)), this.item('部门', b.departmentName || b.departmentID),
          this.item('材质', b.materialCode), this.item('欠料克重', this.grams(b.debtQuantity)), this.item('未结克重', this.grams(b.unsettledQuantity)),
          this.item('预估金价', this.money(b.provisionalUnitPrice, 4)), this.item('预估金额', this.money(b.provisionalAmount)), this.item('发生时间', b.occurredAt),
          this.item('采购订单', b.purchaseOrderCode, this.doc('purchase_order', b.purchaseOrderID, b.purchaseOrderCode)),
          this.item('采购到货单', b.purchaseDeliveryCode, this.doc('purchase_delivery', b.purchaseDeliveryID, b.purchaseDeliveryCode)), this.item('商品', [b.productCode,b.productName].filter(Boolean).join(' · '))
        ]
      }
      if (type === 'payment') {
        const b = p.payment || {}
        return [
          this.item('付款单', b.billCode), this.item('流程名称', b.flowName || b.formName), this.item('状态', statusText(b.status)),
          this.item('当前节点', b.nodeName), this.item('公司', b.companyName), this.item('部门', b.departmentName),
          this.item('申请人', b.createUserName), this.item('申请时间', b.createDate), this.item('付款类型', p.formData.paymentType),
          this.item('付款金额', this.money(p.formData.subtotal)), this.item('表单说明', b.formDesc), this.item('办理人', b.operatorName)
        ]
      }
      if (type === 'purchase_order' || type === 'purchase_delivery') {
        const b = p.bill || {}
        return [
          this.item(type === 'purchase_order' ? '采购订单' : '采购到货单', b.billCode), this.item('公司', b.companyName), this.item('部门', b.departmentName),
          this.item('供应商', this.supplier(b)), this.item('制单日期', b.billDate), this.item('单据类型', b.billType),
          this.item('创建人', b.createUserName), this.item('创建时间', b.createDate), this.item('备注', b.remark)
        ]
      }
      if (type === 'stock_bound') {
        const b = p.stock || {}
        return [this.item('来源单号', b.billCode),this.item('业务类型', b.billType),this.item('公司', b.companyName),this.item('渠道', b.channel),this.item('货品', [b.detailCode,b.detailName].filter(Boolean).join(' · ')),this.item('库存数量', b.stockNumber),this.item('金重', this.grams(b.goldWeight)),this.item('发生时间', b.stockDate || b.createDate)]
      }
      return []
    },
    detailTables() {
      const p = this.payload || {}
      const type = this.activeDocument?.type
      if (type === 'demand') return [
        this.table('需求定额明细', p.details, [this.col('来源类型','demandType',r=>r.demandType==='sales_replenishment'?'销售补料':'供应商结算'),this.col('部门','departmentName'),this.col('材质','materialName'),this.col('申请克重','requestedQuantity'),this.col('批准克重','approvedQuantity'),this.col('内部调拨','internalTransferQuantity'),this.col('外部采购','externalPurchaseQuantity')]),
        this.table('采购单', p.purchases, [this.linkCol('采购单','billCode',r=>this.doc('purchase',r.id,r.billCode)),this.col('材质','materialName'),this.col('采购克重','purchaseQuantity'),this.statusCol('状态','displayStatus','purchase')]),
        this.table('内部调拨', p.transfers, [this.linkCol('调拨单','billCode',r=>this.doc('transfer',r.id,r.billCode)),this.col('调出','fromAccountName'),this.col('调入','toAccountName'),this.col('克重','quantity'),this.statusCol('状态','status','transfer')]),
        this.table('关联欠料', p.debts, [this.linkCol('欠料单','debtCode',r=>this.doc('debt',r.id,r.debtCode)),this.col('供应商','supplierName'),this.col('未结克重','unsettledQuantity'),this.statusCol('状态','status','debt')]),
        this.table('销售库存来源', p.sources, [this.linkCol('来源单号','sourceBillCode',r=>this.doc('stock_bound',r.sourceID,r.sourceBillCode)),this.col('来源类型','sourceType'),this.col('渠道','channel'),this.col('克重','quantity'),this.col('发生时间','occurredAt')])
      ]
      if (type === 'purchase') return [
        this.table('收货批次', p.receipts, [this.linkCol('收货批次','receiptCode',r=>this.doc('receipt',r.id,r.receiptCode)),this.col('收货克重','quantity'),this.col('已分配','allocatedQuantity'),this.col('金价','unitPrice'),this.col('收货时间','receiptDate')]),
        this.table('部门分配', p.allocations, [this.linkCol('收货批次','receiptCode',r=>this.doc('receipt',r.receiptID,r.receiptCode)),this.col('部门账户','accountName'),this.col('材质','accountMaterialName'),this.col('分配克重','quantity'),this.col('分配金额','amount'),this.col('创建时间','createDate')])
      ]
      if (type === 'receipt') return [this.table('部门分配',p.allocations,[this.col('部门账户','accountName'),this.col('材质','accountMaterialName'),this.col('分配克重','quantity'),this.col('金价','unitPrice'),this.col('金额','amount'),this.col('创建时间','createDate')])]
      if (type === 'transfer') return [this.table('调拨过账流水',p.ledgers,[this.col('方向','movementType'),this.col('克重','quantity'),this.col('金价','unitPrice'),this.col('金额','amount'),this.col('过账后克重','balanceQuantity'),this.col('操作人','createUserName'),this.col('发生时间','occurredAt')])]
      if (type === 'debt') return [
        this.table('关联需求',p.demands,[this.linkCol('需求单','billCode',r=>this.doc('demand',r.id,r.billCode)),this.statusCol('状态','status','demand'),this.col('创建时间','createDate')]),
        this.table('还料结清记录',p.settlements,[this.linkCol('采购单','purchaseBillCode',r=>this.doc('purchase',r.purchaseID,r.purchaseBillCode)),this.linkCol('收货批次','receiptCode',r=>this.doc('receipt',r.receiptID,r.receiptCode)),this.col('还料克重','quantity'),this.col('实际金价','actualUnitPrice'),this.col('实际金额','actualAmount'),this.col('结算时间','settlementDate')]),
        this.table('成本修正',p.corrections,[this.col('克重','quantity'),this.col('预估价','originalUnitPrice'),this.col('实际价','actualUnitPrice'),this.col('成本差额','differenceAmount'),this.col('会计月份','accountingMonth'),this.statusCol('状态','status','correction'),this.col('凭证号','voucherNo')])
      ]
      if (type === 'payment') return [this.table('审批记录',p.records,[this.col('节点','nodeName'),this.col('处理人','targetUserName'),this.col('意见','remark'),this.col('处理时间','createDate')])]
      if (type === 'purchase_order' || type === 'purchase_delivery') return [this.table('单据明细',p.lines,[this.col('行号','lineNo'),this.col('商品编号','productCode'),this.col('商品名称','productName'),this.col('数量',type==='purchase_order'?'orderQuantity':'deliveryQuantity'),this.col('金料材质','goldMaterialCode'),this.col('预计金重','estimatedGoldWeight'),this.col('实际金重','actualGoldWeight')])]
      return []
    }
  },
  watch: {
    modelValue(value) { if (value && this.document) { this.activeDocument = { ...this.document }; this.history = []; this.load() } },
    document: { deep: true, handler(value) { if (this.modelValue && value) { this.activeDocument = { ...value }; this.history = []; this.load() } } }
  },
  methods: {
    statusText,
    statusTooltip,
    async load() {
      const doc = this.activeDocument
      if (!doc?.type || !doc?.id) return
      const requestID = ++this.requestID
      this.loading = true
      this.error = ''
      try {
        let response
        if (doc.type === 'demand') response = await getGoldDemandDetail(doc.id)
        else if (doc.type === 'purchase') response = await getGoldPurchaseDetail(doc.id)
        else if (doc.type === 'receipt') response = await getGoldReceiptDetail(doc.id)
        else if (doc.type === 'transfer') response = await getGoldTransferDetail(doc.id)
        else if (doc.type === 'debt') response = await getGoldDebtDetail(doc.id)
        else if (doc.type === 'payment') {
          const [payment,records] = await Promise.all([request({url:'/FlowInstance/'+doc.id,method:'get'}),request({url:'/FlowRecords/',method:'get',params:{instanceId:doc.id}})])
          response = { code: payment.code, content: payment.content, data: { payment: payment.data, records: records.code===200 ? records.data || [] : [], formData: this.parseFormData(payment.data?.formData) } }
        } else if (doc.type === 'purchase_order' || doc.type === 'purchase_delivery') {
          const isOrder = doc.type === 'purchase_order'
          const [bill,lines] = await Promise.all([
            request({url:`/${isOrder?'PurchaseOrderBill':'PurchaseDeliveryBill'}/${doc.id}`,method:'get'}),
            request({url:`/${isOrder?'PurchaseOrderDetail':'PurchaseDeliveryDetail'}/GetDetailList`,method:'get',params:{billId:doc.id}})
          ])
          response = { code: bill.code, content: bill.content, data: { bill: bill.data, lines: lines.code===200 ? lines.data || [] : [] } }
        } else if (doc.type === 'stock_bound') {
          const stock = await request({url:'/StockBound/'+doc.id,method:'get'})
          response = { code: stock.code, content: stock.content, data: { stock: stock.data } }
        } else throw new Error('不支持的单据类型')
        if (requestID !== this.requestID) return
        if (response.code !== 200) throw new Error(response.content || '单据详情加载失败')
        this.payload = response.data || {}
      } catch (error) {
        if (requestID === this.requestID) { this.payload = null; this.error = error?.message || '单据详情加载失败' }
      } finally { if (requestID === this.requestID) this.loading = false }
    },
    openDocument(document) {
      if (!document?.id) return
      if (this.activeDocument) this.history.push({ ...this.activeDocument })
      this.activeDocument = { ...document }
      this.load()
    },
    goBack() { const previous=this.history.pop();if(previous){this.activeDocument=previous;this.load()} },
    reset() { this.requestID++;this.activeDocument=null;this.payload=null;this.error='';this.history=[] },
    parseFormData(value) { if (!value) return {};if(typeof value==='object')return value;try{return JSON.parse(value)}catch{return{}} },
    value(row, prop) { return String(prop).split('.').reduce((current,key)=>current?.[key],row) },
    display(value) { return value === null || value === undefined || value === '' ? '-' : value },
    grams(value) { return value === null || value === undefined ? '-' : `${Number(value).toFixed(4)} g` },
    money(value, precision=2) { return value === null || value === undefined || value === '' || !Number.isFinite(Number(value)) ? '-' : `${Number(value).toFixed(precision)} 元` },
    supplier(row) { return [row.supplierCode,row.supplierName].filter(Boolean).join(' · ') },
    doc(type,id,billCode) { return id ? {type,id,billCode} : null },
    item(label,value,document=null) { return {label,value,document} },
    col(label,prop,format=null) { return {label,prop,format} },
    linkCol(label,prop,document,format=null) { return {label,prop,document,format} },
    statusCol(label,prop,status) { return {label,prop,status} },
    table(title,rows,columns) { return {title,rows:rows||[],columns} }
  }
}
</script>

<style scoped>
.gold-document-detail{min-height:180px}.detail-summary{margin-top:16px}.detail-section{margin-top:22px}.detail-section h3{margin:0 0 10px;font-size:15px}.gold-status-help{display:inline-flex;cursor:help}.gold-business-dialog :deep(.el-dialog__body){max-height:72vh;overflow-y:auto}
</style>
